import React, { useState, useCallback } from 'react';
import type { Lead } from '../../types';
import type { CellCoord, TableColumn, SelectionRect } from '../../types/table';

/**
 * Helper to create a cell key from row and column IDs
 */
export const makeCellKey = (rowId: string, colId: string): string => `${rowId}::${colId}`;

/**
 * Helper to parse a cell key back to coordinates
 */
export const parseCellKey = (key: string): CellCoord | null => {
  const parts = key.split('::');
  if (parts.length !== 2) return null;
  return { rowId: parts[0], colId: parts[1] };
};

export interface UseCellSelectionProps {
  leads: Lead[];
  columns: TableColumn[];
  onRowClick?: (rowId: string) => void;
}

export interface UseCellSelectionReturn {
  // State
  anchor: CellCoord | null;
  selection: Set<string>;
  isDragging: boolean;
  dragStart: CellCoord | null;

  // Actions
  setAnchor: React.Dispatch<React.SetStateAction<CellCoord | null>>;
  setSelection: React.Dispatch<React.SetStateAction<Set<string>>>;
  selectCell: (rowId: string, colId: string, isMulti?: boolean, shouldSetAnchor?: boolean) => void;
  selectRange: (from: CellCoord, to: CellCoord) => void;
  clearSelection: () => void;
  selectAll: () => void;

  // Queries
  isCellSelected: (rowId: string, colId: string) => boolean;
  isAnchorCell: (rowId: string, colId: string) => boolean;
  computeSelectionRectangles: () => SelectionRect[];

  // Drag handlers
  handleCellMouseDown: (e: React.MouseEvent, rowId: string, colId: string) => void;
  handleCellMouseEnter: (rowId: string, colId: string) => void;
  handleCellMouseUp: () => void;

  // Utilities
  scrollCellIntoView: (rowId: string, colId: string) => void;
}

/**
 * Hook for managing Excel/Sheets-style multi-cell selection
 */
export const useCellSelection = ({
  leads,
  columns,
  onRowClick,
}: UseCellSelectionProps): UseCellSelectionReturn => {
  // Selection state
  const [anchor, setAnchor] = useState<CellCoord | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<CellCoord | null>(null);

  /**
   * Scroll selected cell into view, accounting for frozen columns
   */
  const scrollCellIntoView = useCallback((rowId: string, colId: string) => {
    requestAnimationFrame(() => {
      const cellElement = document.querySelector(`[data-cell-id="${rowId}::${colId}"]`) as HTMLElement;
      if (!cellElement) return;

      const scrollContainer = cellElement.closest('.overflow-x-auto') as HTMLElement;
      if (!scrollContainer) return;

      const cellRect = cellElement.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();

      // Account for frozen columns (row number 40px + Name column ~180px)
      const frozenWidth = 40 + 180;
      const visibleLeft = containerRect.left + frozenWidth;
      const visibleRight = containerRect.right;

      // If cell is behind frozen column, scroll right
      if (cellRect.left < visibleLeft) {
        const scrollAmount = visibleLeft - cellRect.left + 20;
        scrollContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
      // If cell is off-screen to the right, scroll left
      else if (cellRect.right > visibleRight) {
        const scrollAmount = cellRect.right - visibleRight + 20;
        scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      }
    });
  }, []);

  /**
   * Select a single cell or toggle in multi-select mode
   */
  const selectCell = useCallback((
    rowId: string,
    colId: string,
    isMulti: boolean = false,
    shouldSetAnchor: boolean = true
  ) => {
    const cellKey = makeCellKey(rowId, colId);
    const coord: CellCoord = { rowId, colId };

    if (isMulti) {
      // Toggle cell in selection
      setSelection(prev => {
        const newSelection = new Set(prev);
        if (newSelection.has(cellKey)) {
          newSelection.delete(cellKey);
        } else {
          newSelection.add(cellKey);
        }
        return newSelection;
      });
      // For multi-select, set anchor if requested
      if (shouldSetAnchor) {
        setAnchor(coord);
        onRowClick?.(rowId);
      }
    } else {
      // Single select - clear previous, set new
      setSelection(new Set([cellKey]));
      setAnchor(coord);
      onRowClick?.(rowId);
      scrollCellIntoView(rowId, colId);
    }
  }, [onRowClick, scrollCellIntoView]);

  /**
   * Select a range of cells from one coordinate to another
   */
  const selectRange = useCallback((fromCoord: CellCoord, toCoord: CellCoord) => {
    const fromRowIdx = leads.findIndex(l => l.id === fromCoord.rowId);
    const toRowIdx = leads.findIndex(l => l.id === toCoord.rowId);
    const fromColIdx = columns.findIndex(c => c.key === fromCoord.colId);
    const toColIdx = columns.findIndex(c => c.key === toCoord.colId);

    if (fromRowIdx === -1 || toRowIdx === -1 || fromColIdx === -1 || toColIdx === -1) return;

    const minRow = Math.min(fromRowIdx, toRowIdx);
    const maxRow = Math.max(fromRowIdx, toRowIdx);
    const minCol = Math.min(fromColIdx, toColIdx);
    const maxCol = Math.max(fromColIdx, toColIdx);

    const newSelection = new Set<string>();
    for (let r = minRow; r <= maxRow; r++) {
      for (let c = minCol; c <= maxCol; c++) {
        const lead = leads[r];
        const col = columns[c];
        if (lead.id && col.key) {
          newSelection.add(makeCellKey(lead.id, col.key));
        }
      }
    }

    setSelection(newSelection);
  }, [leads, columns]);

  /**
   * Clear all selection
   */
  const clearSelection = useCallback(() => {
    setSelection(new Set());
    setAnchor(null);
    setIsDragging(false);
    setDragStart(null);
  }, []);

  /**
   * Select all visible cells
   */
  const selectAll = useCallback(() => {
    const allCells = new Set<string>();
    leads.forEach(lead => {
      if (lead.id) {
        columns.forEach(col => {
          if (col.key) {
            allCells.add(makeCellKey(lead.id, col.key));
          }
        });
      }
    });

    setSelection(allCells);
    // Keep the current anchor or set to first cell
    if (!anchor && leads.length > 0 && columns.length > 0 && leads[0].id) {
      setAnchor({ rowId: leads[0].id, colId: columns[0].key });
      onRowClick?.(leads[0].id);
    }
  }, [leads, columns, anchor, onRowClick]);

  /**
   * Check if a cell is selected
   */
  const isCellSelected = useCallback((rowId: string, colId: string): boolean => {
    return selection.has(makeCellKey(rowId, colId));
  }, [selection]);

  /**
   * Check if a cell is the anchor (active) cell
   */
  const isAnchorCell = useCallback((rowId: string, colId: string): boolean => {
    return anchor?.rowId === rowId && anchor?.colId === colId;
  }, [anchor]);

  /**
   * Compute selection rectangles for marquee overlay
   */
  const computeSelectionRectangles = useCallback((): SelectionRect[] => {
    if (selection.size === 0) return [];

    const selectedCells: { row: number; col: number; rowId: string; colId: string }[] = [];
    selection.forEach(key => {
      const coord = parseCellKey(key);
      if (!coord) return;
      const rowIdx = leads.findIndex(l => l.id === coord.rowId);
      const colIdx = columns.findIndex(c => c.key === coord.colId);
      if (rowIdx !== -1 && colIdx !== -1) {
        selectedCells.push({ row: rowIdx, col: colIdx, rowId: coord.rowId, colId: coord.colId });
      }
    });

    if (selectedCells.length === 0) return [];

    // Create one bounding rectangle
    const minRow = Math.min(...selectedCells.map(c => c.row));
    const maxRow = Math.max(...selectedCells.map(c => c.row));
    const minCol = Math.min(...selectedCells.map(c => c.col));
    const maxCol = Math.max(...selectedCells.map(c => c.col));

    // Check if anchor is in this rect
    const anchorInRect = anchor &&
      selectedCells.some(c => c.rowId === anchor.rowId && c.colId === anchor.colId);

    return [{
      minRow,
      maxRow,
      minCol,
      maxCol,
      isAnchorRect: !!anchorInRect
    }];
  }, [selection, leads, columns, anchor]);

  /**
   * Handle mouse down on a cell (start selection)
   */
  const handleCellMouseDown = useCallback((e: React.MouseEvent, rowId: string, colId: string) => {
    const isShift = e.shiftKey;
    const isCtrlOrCmd = e.ctrlKey || e.metaKey;

    if (isShift && anchor) {
      // Shift+click: Range select from anchor
      selectRange(anchor, { rowId, colId });
    } else if (isCtrlOrCmd) {
      // Ctrl/Cmd+click: Toggle cell in selection
      selectCell(rowId, colId, true, true);
    } else {
      // Regular click: Start new selection
      selectCell(rowId, colId, false, true);
      setDragStart({ rowId, colId });
      setIsDragging(true);
    }
  }, [anchor, selectRange, selectCell]);

  /**
   * Handle mouse enter on a cell during drag
   */
  const handleCellMouseEnter = useCallback((rowId: string, colId: string) => {
    if (isDragging && dragStart) {
      selectRange(dragStart, { rowId, colId });
    }
  }, [isDragging, dragStart, selectRange]);

  /**
   * Handle mouse up (end drag selection)
   */
  const handleCellMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  return {
    // State
    anchor,
    selection,
    isDragging,
    dragStart,

    // Actions
    setAnchor,
    setSelection,
    selectCell,
    selectRange,
    clearSelection,
    selectAll,

    // Queries
    isCellSelected,
    isAnchorCell,
    computeSelectionRectangles,

    // Drag handlers
    handleCellMouseDown,
    handleCellMouseEnter,
    handleCellMouseUp,

    // Utilities
    scrollCellIntoView,
  };
};
